import React, { useEffect, useRef, useState, useCallback } from 'react';

/**
 * "Continue with Google" button, rendered by Google Identity Services.
 *
 * The browser gets a signed ID token (JWT) straight from Google and hands it to
 * `onCredential`; the caller POSTs it to /api/auth/google, which verifies the
 * signature and audience server-side. Nothing here is trusted, and no secret is
 * involved — the client ID is public by design.
 *
 * If no client ID is configured this renders nothing at all. That is deliberate:
 * a misconfigured environment degrades to exactly today's behaviour instead of
 * showing a button that cannot work, and it is also what keeps react-snap's
 * build-time pre-render (no window, no config) from emitting a dead button into
 * the static HTML.
 */

const GSI_SCRIPT_ID = 'google-gsi-script';
const GSI_SRC = 'https://accounts.google.com/gsi/client';

export const getGoogleClientId = () =>
  (typeof window !== 'undefined' && window.__RUNTIME_CONFIG__?.GOOGLE_CLIENT_ID) || '';

// Idempotent script injection — several buttons can mount at once (and remount
// across route changes) but the script is fetched exactly once.
const loadGoogleScript = () => {
  if (typeof window === 'undefined') return Promise.reject(new Error('no window'));
  if (window.google?.accounts?.id) return Promise.resolve();

  if (document.getElementById(GSI_SCRIPT_ID)) {
    return new Promise((resolve, reject) => {
      let waited = 0;
      const i = setInterval(() => {
        if (window.google?.accounts?.id) {
          clearInterval(i);
          resolve();
        } else if ((waited += 50) > 10000) {
          clearInterval(i);
          reject(new Error('Google script load timed out'));
        }
      }, 50);
    });
  }

  return new Promise((resolve, reject) => {
    const s = document.createElement('script');
    s.id = GSI_SCRIPT_ID;
    s.src = GSI_SRC;
    s.async = true;
    s.defer = true;
    s.onload = resolve;
    s.onerror = () => reject(new Error('Failed to load Google Sign-In'));
    document.head.appendChild(s);
  });
};

const GoogleSignInButton = ({
  mode = 'signin',
  onCredential,
  onError,
  disabled = false,
  className = '',
}) => {
  const containerRef = useRef(null);
  const callbackRef = useRef(onCredential);
  const [ready, setReady] = useState(false);
  const [failed, setFailed] = useState(false);

  const clientId = getGoogleClientId();

  // Keep the latest callback without re-initializing GIS on every render.
  useEffect(() => {
    callbackRef.current = onCredential;
  }, [onCredential]);

  const handleCredentialResponse = useCallback((response) => {
    if (response?.credential) {
      callbackRef.current?.(response.credential);
    }
  }, []);

  useEffect(() => {
    if (!clientId) return undefined;

    let cancelled = false;

    loadGoogleScript()
      .then(() => {
        if (cancelled || !containerRef.current) return;

        window.google.accounts.id.initialize({
          client_id: clientId,
          callback: handleCredentialResponse,
          // One Tap / auto-select is deliberately off: it is intrusive and
          // Google is mid-migration to FedCM. Explicit button only.
          auto_select: false,
          cancel_on_tap_outside: true,
          use_fedcm_for_prompt: false,
        });

        containerRef.current.innerHTML = '';
        window.google.accounts.id.renderButton(containerRef.current, {
          type: 'standard',
          theme: 'outline',
          size: 'large',
          // Google's own branding strings — "signup_with" / "signin_with".
          text: mode === 'signup' ? 'signup_with' : 'signin_with',
          shape: 'rectangular',
          logo_alignment: 'left',
          width: containerRef.current.offsetWidth || 360,
        });

        setReady(true);
      })
      .catch((err) => {
        if (cancelled) return;
        setFailed(true);
        onError?.(err);
      });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientId, mode, handleCredentialResponse]);

  // Not configured, or Google could not be reached — show nothing and let the
  // email/password form stand on its own.
  if (!clientId || failed) return null;

  return (
    <div className={`w-full ${className}`}>
      <div
        ref={containerRef}
        className={`flex justify-center w-full transition-opacity ${
          disabled ? 'opacity-50 pointer-events-none' : ''
        }`}
        style={{ minHeight: 44 }}
      />
      {!ready && (
        <div className="flex justify-center items-center h-11 text-sm text-gray-400">
          Loading Google Sign-In…
        </div>
      )}
    </div>
  );
};

export default GoogleSignInButton;
